<script lang="ts">
  import { TwistDivider, TwistGroup, TwistBox } from '@twist/core';
  import { getIconByShape } from '$components/News/ButtonShapes';
  import { type NewsPost, type NewsTag } from '$lib/sanity/schemas';
  import { accessibilityPreferences } from '$lib/stores/accessibility';
  import { newsPostPath } from '$lib/utils/routes';
  import { toFluidVarInput } from '$lib/utils/fluidity';
  import {
    useEncodeDataAttribute,
    type ContentSourceMap,
    type StudioPathLike
  } from '@sanity/svelte-loader';
  import TagText from '$components/News/Common/TagText.svelte';
  import HeadingSizeDecorator, {
    type HeadingSize
  } from '$components/Common/HeadingSizeDecorator.svelte';
  import BlockButton from '$components/Button/BlockButton.svelte';
  import { sanityStudioURL } from '$lib/client';

  interface Props {
    post: NewsPost;
    headlineSize: HeadingSize;
    sourceMap: ContentSourceMap | undefined;
    path?: StudioPathLike;
  }

  let { post, headlineSize, sourceMap, path = [] }: Props = $props();

  let displayTag = $derived(post.tags?.[0] as NewsTag);
  let encodeDataAttribute = $derived(useEncodeDataAttribute(post, sourceMap, sanityStudioURL));

  const spacer = toFluidVarInput(24, 24);
</script>

<div class="item">
  <TwistGroup>
    {#snippet children({ active: groupActive }: { active: boolean })}
      <a href={newsPostPath(post.slug.current)}>
        <TwistDivider color="gray-100" thickness="1px" margin={0} />

        <TwistBox display="flex" marginTop={spacer}>
          <div data-sanity={encodeDataAttribute([...path, 'tags', 0, 'shape'])}>
            <TwistBox marginRight={toFluidVarInput(48, 72)} display="inline-block">
              <BlockButton
                tabindex={-1}
                motion={$accessibilityPreferences.reduceMotion ? 'never' : 'when-active'}
                {groupActive}
                aria-label={post.name}
              >
                {#snippet icon()}
                  {@const SvelteComponent = getIconByShape(displayTag?.shape)}
                  <SvelteComponent />
                {/snippet}
              </BlockButton>
            </TwistBox>
          </div>
          <div>
            <div data-sanity={encodeDataAttribute([...path, 'tags', 0, 'name'])}>
              <TwistBox marginBottom={spacer}>
                <TagText>
                  {displayTag?.name || ''}
                </TagText>
              </TwistBox>
            </div>

            <div data-sanity={encodeDataAttribute([...path, 'name'])}>
              <h3>
                <HeadingSizeDecorator size={headlineSize}>
                  {post.name}
                </HeadingSizeDecorator>
              </h3>
            </div>
          </div>
        </TwistBox>
      </a>
    {/snippet}
  </TwistGroup>
</div>

<style lang="scss">
  @import 'src/scss/variables';

  a {
    text-decoration: none;
    display: block;
  }

  a:focus {
    outline: none;
  }

  h3 {
    font-family: var(--font-family-primary);
    color: $text-default-color;
    font-size: inherit;
  }
</style>
